import React, { useEffect, useState } from 'react';
import { BellOff, CheckCircle2, History, Loader2, Send, AlertTriangle } from 'lucide-react';

import { Button } from '../../ui-v2';
import { Modal } from '../../ui-v2.5';
import { previsualizarRecordatorioLote, reavisarLote } from '../api';

/**
 * «Volver a avisar» a las personas de un lote que siguen sin cita.
 *
 * Al abrir pide al servidor a quién iría: las del lote que fueron avisadas y no han reservado,
 * menos quien tiene silenciado este aviso (email e in-app) y quien ya recibió hoy el recordatorio
 * automático. Lo enseña con la lista (desplegable) y los envíos anteriores del lote. Si ahora no se
 * puede (campaña no Activa, sin fecha límite o pasada, ya enviado hoy), dice por qué y no deja
 * enviar. Tras enviar, el resultado: a cuántas llegó, cuántas fallaron y quiénes.
 *
 * Nombres de personas y del lote: los escribe un usuario y se pintan como TEXTO, nunca HTML.
 */

export const personas = (n) => `${n} ${n === 1 ? 'persona' : 'personas'}`;
const segun = (n, una, varias) => (n === 1 ? una : varias);

const fechaHora = (iso) => {
  if (!iso) return '—';
  const f = new Date(iso);
  if (Number.isNaN(f.getTime())) return String(iso);
  return f.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function ListaPersonas({ titulo, lista }) {
  if (!lista || !lista.length) return null;
  return (
    <details className="rounded-v25-sm border border-v25-line">
      <summary className="cursor-pointer select-none px-2.5 py-1.5 text-xs text-v25-ink-700">
        {titulo} ({lista.length})
      </summary>
      <ul className="max-h-48 overflow-y-auto border-t border-v25-line px-2.5 py-1.5 text-xs text-v25-ink-700" data-lista={titulo}>
        {lista.map((p) => <li key={p.persona_id} className="py-0.5">{p.nombre}</li>)}
      </ul>
    </details>
  );
}

function resultadoEnvio(e) {
  if (e.enviados == null) return `${personas(e.destinatarios)} · resultado sin registrar`;
  const partes = [`${e.enviados} ${e.enviados === 1 ? 'enviado' : 'enviados'}`];
  if (e.fallidos) partes.push(`${e.fallidos} con fallo`);
  if (e.silenciados) partes.push(`${e.silenciados} ${e.silenciados === 1 ? 'silenciada' : 'silenciadas'}`);
  if (e.ya_avisados_hoy) partes.push(`${e.ya_avisados_hoy} ya ${e.ya_avisados_hoy === 1 ? 'avisada' : 'avisadas'} por el automático`);
  return partes.join(' · ');
}

function Historial({ envios }) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
        <History className="h-3.5 w-3.5" /> Envíos anteriores de este lote
      </h4>
      {!envios || envios.length === 0 ? (
        <p className="text-xs text-v25-ink-500">Aún no se ha vuelto a avisar a este lote.</p>
      ) : (
        <ul className="space-y-1 text-xs" data-historial>
          {envios.map((e) => (
            <li key={e.id} className="flex flex-wrap justify-between gap-x-3 rounded-v25-sm bg-v25-surface-sunken px-2.5 py-1.5">
              <span>{fechaHora(e.enviado_en)} · {e.enviado_por ? e.enviado_por.nombre : 'no consta quién'}</span>
              <span className="tabular-nums text-v25-ink-500">{resultadoEnvio(e)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ModalReavisarLote({ campanaId, lote, onClose, onEnviado }) {
  const [previa, setPrevia] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (!lote) return undefined;
    let vivo = true;
    setPrevia(null); setError(null); setResultado(null);
    previsualizarRecordatorioLote(campanaId, lote.id)
      .then((p) => { if (vivo) setPrevia(p); })
      .catch((e) => { if (vivo) setError(e?.response?.data?.error || 'No se pudo preparar el aviso.'); });
    return () => { vivo = false; };
  }, [campanaId, lote]);

  async function enviar() {
    setEnviando(true); setError(null);
    try {
      const r = await reavisarLote(campanaId, lote.id);
      setResultado(r);
      onEnviado?.();
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo enviar el aviso.');
    } finally {
      setEnviando(false);
    }
  }

  if (!lote) return null;
  const n = previa?.totales?.destinatarios ?? 0;
  const puedeEnviar = !!previa && !previa.bloqueo && n > 0 && !resultado;

  let pie;
  if (resultado) {
    pie = <Button onClick={onClose}>Cerrar</Button>;
  } else {
    pie = (
      <>
        <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
        {puedeEnviar && (
          <Button onClick={enviar} disabled={enviando}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar a {personas(n)}
          </Button>
        )}
      </>
    );
  }

  return (
    <Modal
      open
      onClose={enviando ? () => {} : onClose}
      size="md"
      title={`Volver a avisar · Lote ${lote.numero} · ${lote.nombre}`}
      subtitle="Solo a quienes siguen sin cita"
      footer={pie}
    >
      <div className="space-y-4 text-sm text-v25-ink-700">
        {error && (
          <div role="alert" className="flex gap-2 rounded-v25-sm border border-red-100 bg-red-50 p-2.5 text-xs text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" /> <span>{error}</span>
          </div>
        )}

        {!previa && !error && (
          <p className="text-v25-ink-500"><Loader2 className="mr-2 inline-block h-4 w-4 animate-spin" />Contando a quién iría…</p>
        )}

        {resultado && (
          <div data-resultado className="space-y-2">
            <p className="flex items-center gap-2 font-medium text-v25-ink-900">
              <CheckCircle2 className="h-4 w-4 text-v25-active-fg" />
              Recordatorio enviado a {personas(resultado.totales.enviados)}.
            </p>
            <ul className="space-y-0.5 text-xs text-v25-ink-500">
              {resultado.totales.fallidos > 0 && <li className="text-red-700">{personas(resultado.totales.fallidos)}: no se {segun(resultado.totales.fallidos, 'le', 'les')} pudo enviar.</li>}
              {resultado.totales.silenciados > 0 && <li>{personas(resultado.totales.silenciados)} lo {segun(resultado.totales.silenciados, 'tiene', 'tienen')} silenciado y no lo {segun(resultado.totales.silenciados, 'ha', 'han')} recibido.</li>}
              {resultado.totales.ya_avisados_hoy > 0 && <li>{personas(resultado.totales.ya_avisados_hoy)} ya {segun(resultado.totales.ya_avisados_hoy, 'había', 'habían')} recibido hoy el recordatorio automático.</li>}
            </ul>
            <ListaPersonas titulo="No se pudo enviar a" lista={resultado.fallidas} />
          </div>
        )}

        {previa && !resultado && (
          <>
            {previa.bloqueo ? (
              <div role="status" data-bloqueo={previa.bloqueo.codigo} className="rounded-v25-sm border border-v25-line bg-v25-surface-sunken p-2.5 text-xs text-v25-ink-700">
                {previa.bloqueo.error}
              </div>
            ) : (
              <p>
                Se enviará el recordatorio <strong>«Recuerda reservar tu cita»</strong> a{' '}
                <strong className="text-v25-ink-900" data-destinatarios>{personas(n)}</strong> de este lote que ya fueron
                avisadas y aún no han reservado. Quien tiene cita, la ha hecho, está cancelada o aún no fue avisada no recibe nada.
              </p>
            )}
            {(previa.totales.silenciados > 0 || previa.totales.ya_avisados_hoy > 0) && (
              <ul className="space-y-0.5 text-xs text-v25-ink-500">
                {previa.totales.silenciados > 0 && (
                  <li className="flex items-center gap-1.5" data-silenciadas>
                    <BellOff className="h-3.5 w-3.5" /> {personas(previa.totales.silenciados)} lo {segun(previa.totales.silenciados, 'tiene', 'tienen')} silenciado en sus preferencias: no lo {segun(previa.totales.silenciados, 'recibirá', 'recibirán')}.
                  </li>
                )}
                {previa.totales.ya_avisados_hoy > 0 && (
                  <li data-avisadas-hoy>{personas(previa.totales.ya_avisados_hoy)} ya {segun(previa.totales.ya_avisados_hoy, 'recibió', 'recibieron')} hoy el recordatorio automático: no se {segun(previa.totales.ya_avisados_hoy, 'le', 'les')} repite.</li>
                )}
              </ul>
            )}
            <div className="space-y-1.5">
              <ListaPersonas titulo="Lo recibirán" lista={previa.destinatarias} />
              <ListaPersonas titulo="Lo tienen silenciado" lista={previa.silenciadas} />
              <ListaPersonas titulo="Ya avisadas hoy por el automático" lista={previa.ya_avisadas_hoy} />
            </div>
            <p className="text-xs text-v25-ink-500">Una vez al día por lote. Las cifras se vuelven a contar al enviar.</p>
            <Historial envios={previa.envios} />
          </>
        )}
      </div>
    </Modal>
  );
}
